<template>
  <div class="gift-card-container">
    <template v-if="isLoading">
      <v-progress-circular
        :size="70"
        :width="7"
        color="primary"
        indeterminate
      />
    </template>
    <div v-show="!isLoading">
      <div v-show="step === 1" class="step-1">
        <div class="gift-card-header" v-if="getGiftcardDesigns.length > 0">
          <div class="gift-card-header-title">
            {{ $vuetify.lang.t("$vuetify.giftcard.chooseDesign") }}
          </div>
          <div class="gift-card-header-sub">
            {{ $vuetify.lang.t("$vuetify.giftcard.chooseDesignSub") }}
          </div>
        </div>
        <v-layout row wrap class="gift-card-designs-container">
          <v-flex
            xs6
            sm6
            md3
            lg3
            xl3
            class="gift-card-design-wrapper"
            v-for="design in getGiftcardDesigns"
            :key="design.code"
          >
            <v-img
              class="gift-card-design"
              :key="design.code"
              :src="getDesignUrlByCode(design.code)"
              @click="onImageSelect(design)"
              :alt="$vuetify.lang.t('$vuetify.giftcard.giftCardDesign')"
            ></v-img>
          </v-flex>
        </v-layout>
      </div>

      <div v-show="build && step === 2" class="GiftCard">
        <div class="gift-card-builder">
          <div
            v-if="cardDetails.designCode"
            class="gift-card-img-close-container"
            @click="step = 1"
          >
            <v-icon>arrow_back</v-icon>
            <span>
              {{ $vuetify.lang.t("$vuetify.giftcard.backToAllGiftCards") }}
            </span>
          </div>

          <gift-card-design
            :design-code="cardDetails.designCode"
          />
          <spacer size="m" />

          <section class="gc-section">
            <div class="gc-section-title">
              {{ $vuetify.lang.t("$vuetify.forms.giftcard.selectDeliveryMethod") }}
            </div>
            <div style="height: 16px;width: 100%;"></div>
            <div class="gc-pill-group">
              <button
                type="button"
                class="gc-pill"
                :class="{ 'is-active': sendMethod === 'email' }"
                :style="sendMethod === 'email' ? { color: btnTextColor } : {}"
                @click="setSendMethod('email')"
              >
                {{ $vuetify.lang.t("$vuetify.forms.giftcard.email") }}
              </button>
              <button
                type="button"
                class="gc-pill"
                :class="{ 'is-active': sendMethod === 'phone' }"
                :style="sendMethod === 'phone' ? { color: btnTextColor } : {}"
                @click="setSendMethod('phone')"
              >
                {{ $vuetify.lang.t("$vuetify.forms.giftcard.phone") }}
              </button>
            </div>
          </section>

          <div style="height: 48px;width: 100%;"></div>

          <section class="gc-section">
            <div class="gc-section-title">
              {{ $vuetify.lang.t("$vuetify.forms.giftcard.giftAmount") }}
            </div>
            <spacer size="m"></spacer>
            <div class="gc-promotions-subtitle">
              <template v-if="build.allowCustomizedAmount">
                {{ $vuetify.lang.t("$vuetify.forms.giftcard.selectOrEnterAmount", currencySymbol + build.minAmount, currencySymbol + build.maxAmount) }}
              </template>
              <template v-else> 
                {{ $vuetify.lang.t("$vuetify.forms.giftcard.selectAnAmount") }}
              </template>
            </div>
            <template v-if="promotions.length > 0">
              <div style="height: 16px;width: 100%;"></div>
              <div class="gc-promotions-box">
                <div class="gc-promotions-title">
                  {{ $vuetify.lang.t("$vuetify.giftcard.currentPromotions") }}
                </div>
                <div
                  v-for="(promo, index) in promotions"
                  :key="index"
                  class="gc-promo-item"
                  v-html="promo"
                ></div>
              </div>
            </template>

            <div style="height: 16px;width: 100%;"></div>
            <div class="gc-amount-buttons">
              <button
                v-for="amount in build.amounts"
                :key="amount"
                type="button"
                class="gc-amount-btn"
                :class="{ 'is-selected': cardDetails.amount === amount }"
                @click="selectPresetAmount(amount)"
              >
                {{ currencySymbol }}{{ amount }}
              </button>
              <button
                v-if="showOther"
                type="button"
                class="gc-amount-btn"
                :class="{
                  'is-selected': (isCustomAmountSelected || cardDetails.otherAmount) && !cardDetails.amount,
                }"
                @click="selectCustomAmount"
              >
                {{ $vuetify.lang.t("$vuetify.giftcard.custom") }}
              </button>
            </div>

            <spacer size="m"></spacer>

            <v-text-field
              v-if="showOther"
              ref="customAmountInput"
              v-model="cardDetails.otherAmount"
              :placeholder="$vuetify.lang.t('$vuetify.forms.giftcard.enterCustomDollarAmount')"
              type="number"
              :max="build.maxAmount"
              :min="build.minAmount || 0"
              @input="handleCustomAmountInput"
              :error-messages="errors.otherAmount"
              filled
              rounded
              class="gc-custom-amount-input"
            />
          </section>
          <spacer size="m"></spacer>
          <spacer size="xl"></spacer>

          <section class="gc-section">
            <div class="gc-section-title">
              {{
                $vuetify.lang.t("$vuetify.forms.giftcard.whoAreYouGiftingTo")
              }}
            </div>

            <spacer size="m"></spacer>

            <masked-input
              ref="From"
              :label="$vuetify.lang.t('$vuetify.forms.giftcard.recipientName')"
              required
              validation="nonempty"
              :hint="
                $vuetify.lang.t('$vuetify.forms.giftcard.recipientNameHint')
              "
              icon="person_outline"
              :error-messages="errors['nameError']"
              :raw.sync="cardDetails.receiverName"
              key="RecipientName"
            />

            <spacer size="m"></spacer>

            <masked-input
              v-if="sendMethod === 'email'"
              ref="RecipientEmail"
              :label="$vuetify.lang.t('$vuetify.forms.giftcard.recipientEmail')"
              :hint="$vuetify.lang.t('$vuetify.forms.giftcard.emailHint')"
              icon="mail_outline"
              validation="validateEmail"
              :required="sendMethod === 'email'"
              :error-messages="errors['emailError']"
              :raw.sync="cardDetails.receiverEmail"
              key="RecipientEmail"
            />

            <masked-input
              v-else
              ref="RecipientPhone"
              :label="$vuetify.lang.t('$vuetify.forms.giftcard.recipientMobilePhone')"
              :hint="$vuetify.lang.t('$vuetify.forms.giftcard.phoneHint')"
              icon="phone"
              mask="phoneNumber"
              :required="sendMethod === 'phone'"
              validation="validatePhone"
              :error-messages="errors['phoneError']"
              :raw.sync="cardDetails.receiverMobile"
              limitRawLength="10"
              key="RecipientPhone"
            />
          </section>

          <spacer size="m"></spacer>
          <spacer size="xl"></spacer>

          <section class="gc-section">
            <div class="gc-section-title">
              {{ $vuetify.lang.t("$vuetify.forms.giftcard.personalNote") }}
            </div>

            <spacer size="m"></spacer>

            <masked-input
              ref="From"
              :label="$vuetify.lang.t('$vuetify.forms.giftcard.senderName')"
              :hint="$vuetify.lang.t('$vuetify.forms.giftcard.nameHint')"
              icon="person_outline"
              :raw.sync="cardDetails.senderName"
            />

            <spacer size="m"></spacer>

            <v-textarea
              name="input-1-1"
              :label="$vuetify.lang.t('$vuetify.forms.giftcard.message')"
              :hint="$vuetify.lang.t('$vuetify.forms.giftcard.messageHint')"
              prepend-inner-icon="chat_bubble_outline"
              rows="2"
              filled
              rounded
              v-model="cardDetails.customMessage"
            />
          </section>

          <section
            class="gc-section"
            v-if="!!termsAndConditions"
            id="termsAndConditions"
          >
            <div class="gc-section-title">
              {{
                $vuetify.lang.t("$vuetify.forms.giftcard.termsAndConditions")
              }}
            </div>

            <div v-html="termsAndConditions"></div>
          </section>

          <spacer size="m"></spacer>
          <spacer size="xl"></spacer>

          <div class="row center-xs">
            <v-btn
              min-width="250"
              style="padding:0 40px; text-transform: none; margin-bottom: 10px;"
              rounded
              color="primary"
              :disabled="!readyToProceed"
              @click="moveToCheckout"
            >
              <v-icon style="margin-right: 10px;">lock</v-icon>
              {{
                $vuetify.lang.t("$vuetify.forms.giftcard.checkOutAndSendGift")
              }}
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import maskedInput from "../common/maskedInput";
// import ButtonControl from '../common/ButtonControl'
import Spacer from "../common/Spacer.vue";
import GiftCardDesign from "../feature/GiftCardDesign.vue";
import { get } from "lodash";
import { mapMutations, mapActions, mapGetters } from "vuex";
import { primaryTextColor } from "@/plugins/util";

export default {
  components: {
    maskedInput,
    // ButtonControl,
    GiftCardDesign,
    Spacer,
  },

  data() {
    return {
      errors: {},
      snackbar: false,
      sendMethod: "email",
      step: 1,
      isLoading: true,
      wheelHandler: null,
      isCustomAmountSelected: false,
    };
  },

  computed: {
    btnTextColor() { return primaryTextColor(this.$store.getters.getManifest?.meta?.primary) },
    infoEntered() {
      // We need at least one of the two reveirer info but not both
      return (
        this.cardAmount > 0 &&
        !!this.cardDetails.receiverName &&
        !!(this.cardDetails.receiverMobile || this.cardDetails.receiverEmail)
      );
    },
    readyToProceed() {
      if (this.infoEntered) {
        const _invalidFields = Object.keys(this.errors).length;

        if (_invalidFields === 0) {
          // Ideally, the entire form is valid
          return true;
        } else if (_invalidFields === 1) {
          // Since we only need the receivers email or phone number, 1 of the 2 errors is acceptable
          return !!(!!this.errors.phoneError ^ !!this.errors.emailError);
        }

        return false;
      }

      return false;
    },

    showOther() {
      return !!this.customization?.allowCustomizedAmount;
    },

    termsAndConditions() {
      const terms = get(
        this.build,
        "settings.rwaSettings.customizedGiftcardMessage.translations"
      );

      if (terms) return this.$options.filters.translate(terms);
      return null;
    },

    bonuses() {
      return get(this.build, "bonusesForReceiver.bonuses", []);
    },

    promotions() {
      if (!this.bonuses || this.bonuses.length === 0) return [];

      return this.bonuses
        .filter((bonus) => bonus.bonusCash > 0)
        .map((bonus) => {
          const bonusAmount = this.currency(bonus.bonusCash);
          const minAmount = this.currency(bonus.amountDepositMin);
          const maxAmount =
            bonus.amountDepositMax > bonus.amountDepositMin
              ? ` - ${this.currency(bonus.amountDepositMax)}`
              : bonus.amountDepositMax === -1
              ? " or more"
              : "";
          return `<strong style="color:inherit;font-weight:600;">${bonusAmount} bonus</strong> with ${minAmount}${maxAmount} in gift card online`;
        });
    },

    ...mapGetters({
      getDesignUrlByCode: "getDesignUrlByCode",
      storeId: "getStoreID",
      build: "getBuild",
      cardDetails: "getCardDetails",
      billingInfo: "getBillingInfo",
      currencySymbol: "getCurrenySymbol",
      cardAmount: "getCardAmont",
      customization: "getGiftcardCustomization",
      origin: "origin",
      getStore: "getStore",
      getGiftcardDesigns: "getGiftcardDesigns",
    }),
  },

  methods: {
    onImageSelect(design) {
      this.step = 2;
      this.setCardDetailsDesignCode(design.code);
    },
    setSendMethod(method) {
      this.sendMethod = method;
      const oppositeError = method === "email" ? "phoneError" : "emailError";
      this.$delete(this.errors, oppositeError);
      this.setCardDetails({
       ...this.cardDetails,
        receiverMobile: null,
        receiverEmail: null,
      });
    },
    currency(val, showDec = false) {
      return this.$options.filters.currency(val, showDec);
    },

    selectPresetAmount(amount) {
      this.cardDetails.amount = amount;
      this.cardDetails.otherAmount = null;
      this.isCustomAmountSelected = false;
      this.$delete(this.errors, "otherAmount");
    },

    selectCustomAmount() {
      this.cardDetails.amount = null;
      this.isCustomAmountSelected = true;
      this.$nextTick(() => {
        // Use ref to access the v-text-field component, then get the input element
        if (this.$refs.customAmountInput) {
          const input = this.$refs.customAmountInput.$el.querySelector('input');
          if (input) {
            input.focus();
          }
        }
      });
    },

    handleCustomAmountInput(value) {
      this.cardDetails.otherAmount = value;
      this.cardDetails.amount = null;
      this.validateCustomAmount();
    },

    validateCustomAmount() {
      if (
        !this.cardDetails.otherAmount ||
        this.cardDetails.otherAmount === ""
      ) {
        this.$set(
          this.errors,
          "otherAmount",
          this.$vuetify.lang.t("$vuetify.forms.cardPrompt")
        );
      } else if (this.cardDetails.otherAmount > this.build.maxAmount) {
        this.$set(
          this.errors,
          "otherAmount",
          this.$vuetify.lang.t(
            "$vuetify.forms.amountMax",
            this.currency(this.build.maxAmount)
          )
        );
      } else if (
        this.cardDetails.otherAmount < (this.build.minAmount || 0.01)
      ) {
        this.$set(
          this.errors,
          "otherAmount",
          this.$vuetify.lang.t(
            "$vuetify.forms.amountMin",
            this.currency(this.build.minAmount || 0.01)
          )
        );
      } else {
        this.$delete(this.errors, "otherAmount");
      }
    },

    preventWheelOnNumberInput() {
      this.$nextTick(() => {
        if (this.$refs.customAmountInput) {
          const input = this.$refs.customAmountInput.$el.querySelector('input[type="number"]');
          if (input) {
            // Remove existing event listeners
            if (this.wheelHandler) {
              input.removeEventListener('wheel', this.wheelHandler);
              input.removeEventListener('mousewheel', this.wheelHandler);
            }
            
            // Create new event handler
            this.wheelHandler = (e) => {
              e.preventDefault();
            };
            
            // Bind event listeners
            input.addEventListener('wheel', this.wheelHandler, { passive: false });
            input.addEventListener('mousewheel', this.wheelHandler, { passive: false });
          }
        }
      });
    },

    moveToCheckout() {
      if (!this.sendMethod) {
        this.$bus.$emit("showAlert", `Please select a send method`, "error");
        return;
      }
      if (this.errors[`${this.sendMethod}Error`]) {
        this.$bus.$emit(
          "showAlert",
          `Please correct the receivers ${
            this.sendMethod === "phone" ? "mobile number" : "e-mail"
          } in order to use that method`,
          "error"
        );
        return;
      }

      if (this.sendMethod === "phone") {
        delete this.cardDetails.receiverEmail;
      } else if (this.sendMethod === "email") {
        delete this.cardDetails.receiverMobile;
      }
      this.setCardDetails(this.cardDetails);

      this.$router.push({
        name: "Checkout",
        params: { storeId: this.storeId },
      });
    },

    applyDefaults() {
      if (this.cardDetails) {
        if (
          this.cardDetails.customMessage === null ||
          this.cardDetails.customMessage === undefined
        )
          this.cardDetails.customMessage = this.$vuetify.lang.t(
            "$vuetify.giftcard.defaultMessage"
          );
      }
    },

    toBottom() {
      this.$vuetify.goTo("#CardPreview");
    },

    checkValidation() {
      for (const ref in this.$refs) {
        // Run the validation method of each input
        if (ref && this.$refs[ref] && "validate" in this.$refs[ref])
          this.$refs[ref].validate();
      }
    },

    ...mapMutations({
      setBuild: "setBuild",
      setCardDetails: "setCardDetails",
      setCardDetailsDesignCode: "setCardDetailsDesignCode",
      setGiftcardDesigns: "setGiftcardDesigns",
    }),
    ...mapActions({
      fetchGiftcardStore: "fetchGiftcardStore",
    }),
  },

  watch: {
    showOther(newVal) {
      if (newVal) {
        this.preventWheelOnNumberInput();
      }
    },
  },

  async mounted() {
    await this.fetchGiftcardStore();
    const designs = this.build?.giftcardDesignSettings?.designs;
    this.setGiftcardDesigns(designs);
    if (designs.length === 0) {
      this.step = 2;
    }
    this.isLoading = false;
    this.preventWheelOnNumberInput();
  },
};
</script>

<style scoped>
.gift-card-container {
  .GiftCard {
    max-width: 636px;
    margin: 0 auto;
    padding: 0 8px;
    padding-top: 0;
  }

  .gift-card-builder {
    display: flex;
    flex-direction: column;
    margin-top: 0;
  }

  .gift-card-img-close-container {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
  }

  .gc-section {
    background: transparent;
    padding: 0;
    text-align: left;
  }

  .gc-section-divider {
    height: 8px;
    border-bottom: 1px solid #d9d9d9;
  }

  .gc-section-title {
    font-size: 24px;
    line-height: 36px;
    font-weight: 700;
    color: #111827;
  }

  .gc-section-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 16px;
  }

  .gc-promotions-box {
    background: #ffffff;
    border-radius: 16px;
    padding: 16px;
    border: 1px solid #d9d9d9;
  }

  .gc-promotions-title {
    font-size: 17px;
    font-weight: 700;
    color: #000000;
    margin-bottom: 8px;
  }

  .gc-promotions-subtitle {
    font-size: 17px;
    color: #49454f;
    line-height: 24px;
  }

  .gc-promo-item {
    font-size: 17px;
    line-height: 24px;
    color: var(--primary-color, #16a34a);
    margin-bottom: 8px;
  }

  .gc-promo-item:last-child {
    margin-bottom: 0;
  }

  .gc-amount-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .gc-amount-btn {
    flex: 1;
    min-width: 80px;
    border: none;
    border-radius: 12px;
    padding: 14px 20px;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    background: #f3f4f6;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .gc-amount-btn:hover {
    background: #e5e7eb;
  }

  .gc-amount-btn.is-selected {
    background: var(--primary-color, #16a34a);
    color: #ffffff;
  }

  /* .gc-custom-input {
    margin-top: 16px;
  }

  .gc-custom-input .v-input__control .v-input__slot {
    background: #f3f4f6 !important;
    border-radius: 12px;
  }

  .gc-custom-input {
    padding-left: 0;
    margin-bottom: 0;
  }

  .gc-custom-input input::placeholder {
    color: #9ca3af;
  } */

  .gc-pill-group {
    display: flex;
    gap: 12px;
    background: #f4f6fb;
    padding: 8px;
    border-radius: 38px;
  }

  .gc-pill {
    flex: 1;
    border: none;
    border-radius: 32px;
    padding: 12px 8px;
    font-size: 16px;
    font-weight: 600;
    color: #0f172a;
    background: transparent;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: none;
  }

  .gc-pill.is-active {
    background: var(--primary-color, #16a34a);
  }

  .gc-amount-row {
    margin-top: 16px;
  }

  /* Hide number input spinner arrows */
  .gc-custom-amount-input /deep/ input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .gc-custom-amount-input >>> input[type="number"] {
    -moz-appearance: textfield;
  }

  .gc-bonus {
    margin-top: 24px;
  }

  #cardAmount button,
  #otherAmountInput {
    min-width: 92px !important;
    margin: 5px 0px 5px 10px !important;
  }

  #otherAmountInput {
    padding-left: 0;
    max-width: 110px;
  }

  #termsAndConditions h3 {
    margin-top: 35px;
    text-align: center;
  }

  #termsAndConditions div {
    font-size: 14px;
    color: grey;

    padding: 10px 10px 20px;
  }
  .step-1 {
    text-align: center;

    .gift-card-header {
      margin-bottom: 16px;

      .gift-card-header-title {
        font-size: 36px;
        font-weight: 600;
      }

      .gift-card-header-sub {
        font-size: 24px;
      }
    }

    .gift-card-designs-container {
      padding: 0 16px;

      .gift-card-design-wrapper {
        display: flex;
        justify-content: center;
        padding: 8px;
        cursor: pointer;

        .gift-card-design {
          border-radius: 20px;
          box-shadow: 0px 2.4px 4.8px 0px rgba(0, 0, 0, 0.25);
          transition: transform 0.3s ease;

          &:hover {
            transform: translateY(-16px);
          }
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    .step-1 {
      .gift-card-header {
        padding-top: 24px;

        .gift-card-header-title {
          font-size: 24px;
        }

        .gift-card-header-sub {
          font-size: 17px;
        }
      }

      .gift-card-designs-container {
        padding: 0 8px;

        .gift-card-design-wrapper {
          padding: 4px;

          .gift-card-design {
            border-radius: 10px;
            width: 90%;
          }
        }
      }
    }
  }
}
</style>
